import { envConfig } from "@/config/env";
import apiClient, { withDefaultError } from "@/lib/apiClient";
import { unwrapAuthPayload } from "@/lib/auth/unwrapAuthPayload";
import type { CreateUserRequestInterface } from "@/lib/schemas/createUserRequestSchema";
import type {
  ActiveUsersByHourResponse,
  ApiResponse,
  ApproveTrainerCertificationRequest,
  CertificationCriteria,
  CertificationRequestsResponse,
  CertificationReviewQueue,
  CreateUserResponse,
  DeleteUserResponse,
  LoginData,
  LoginResponse,
  PlatformTrainerDetails,
  RecentUsersResponse,
  RevokeTrainerCertificationRequest,
  TrainerCertificationStatus,
  TrainerDetailsResponse,
  TrainerProfile,
  UpdateUserRequest,
  UpdateUserResponse,
  UpdateCertificationCriteriaRequest,
  UserCountResponse,
  UserDetailsResponse,
  UsersListResponse,
} from "@/types/api";

async function loginUser(data: LoginData): Promise<LoginResponse> {
  const response = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      (error as { message?: string }).message || "Login failed",
    );
  }

  const body = await response.json();
  return unwrapAuthPayload<LoginResponse>(
    body,
    "Impossible de se connecter",
  );
}

// Get users list with pagination and filtering
async function getUsersList(
  page?: number,
  limit?: number,
  search?: string,
  role?: string,
  status?: string,
): Promise<ApiResponse<UsersListResponse>> {
  const params = new URLSearchParams();
  if (page) params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());
  if (search) params.append("search", search);
  if (role) params.append("role", role);
  if (status) params.append("status", status);

  const url = `${API_URL}/admin/users${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to get users list data");
  }

  return response.json();
}

const API_URL = envConfig.apiBaseUrl;
async function getUserDetails(
  userId: number,
): Promise<ApiResponse<UserDetailsResponse>> {
  const response = await apiClient.get<UserDetailsResponse>(
    `/admin/users/${userId}/details`,
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to get user details");
  }

  return response;
}

// Trainer details API functions

// Get trainer details by ID
async function getTrainerDetails(
  trainerId: number,
): Promise<ApiResponse<TrainerDetailsResponse>> {
  const response = await apiClient.get<TrainerDetailsResponse>(
    `/admin/trainers/${trainerId}/details`,
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to get trainer details");
  }
  return response;
}

async function getCertificationCriteria(): Promise<
  ApiResponse<CertificationCriteria>
> {
  const response = await apiClient.get<CertificationCriteria>(
    "/admin/certification/criteria",
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to get certification criteria");
  }
  return response;
}

async function updateCertificationCriteria(
  data: UpdateCertificationCriteriaRequest,
): Promise<ApiResponse<CertificationCriteria>> {
  const response = await apiClient.put<CertificationCriteria>(
    "/admin/certification/criteria",
    {
      body: data,
    },
  );
  if (!response.ok) {
    return withDefaultError(
      response,
      "Failed to update certification criteria",
    );
  }
  return response;
}

async function getCertificationRequests(
  status?: "PENDING" | "APPROVED" | "REJECTED",
): Promise<ApiResponse<CertificationRequestsResponse>> {
  const query = status ? `?status=${status}` : "";
  const response = await apiClient.get<CertificationRequestsResponse>(
    `/admin/certification/requests${query}`,
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to get certification requests");
  }
  return response;
}

async function getTrainerCertificationStatus(
  trainerId: number,
): Promise<ApiResponse<TrainerCertificationStatus>> {
  const response = await apiClient.get<TrainerCertificationStatus>(
    `/admin/trainers/${trainerId}/certification`,
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to get trainer certification");
  }
  return response;
}

async function approveTrainerCertification(
  trainerId: number,
  data: ApproveTrainerCertificationRequest,
): Promise<ApiResponse<TrainerCertificationStatus>> {
  const response = await apiClient.post<TrainerCertificationStatus>(
    `/admin/trainers/${trainerId}/certification/approve`,
    {
      body: data,
    },
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to approve certification");
  }
  return response;
}

/**
 * Set or withdraw the manual quality review on its own.
 *
 * Separate from the approval call: the flag feeds the eligibility
 * computation, so it has to be settable before a trainer is certified.
 */
async function setTrainerQualityApproval(
  trainerId: number,
  approved: boolean,
): Promise<ApiResponse<TrainerCertificationStatus>> {
  const response = await apiClient.post<TrainerCertificationStatus>(
    `/admin/trainers/${trainerId}/certification/quality`,
    {
      body: { approved },
    },
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to update quality approval");
  }
  return response;
}

/** Trainers awaiting a certification decision, in both directions. */
async function getCertificationReviewQueue(): Promise<
  ApiResponse<CertificationReviewQueue>
> {
  const response = await apiClient.get<CertificationReviewQueue>(
    "/admin/certification/review-queue",
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to load the review queue");
  }
  return response;
}

async function revokeTrainerCertification(
  trainerId: number,
  data?: RevokeTrainerCertificationRequest,
): Promise<ApiResponse<{ trainerId: number; isCertified: boolean }>> {
  const response = await apiClient.post<{
    trainerId: number;
    isCertified: boolean;
  }>(`/admin/trainers/${trainerId}/certification/revoke`, {
    body: data,
  });
  if (!response.ok) {
    return withDefaultError(response, "Failed to revoke certification");
  }
  return response;
}

async function rejectCertificationRequest(
  requestId: number,
): Promise<ApiResponse<{ id: number; status: string }>> {
  const response = await apiClient.post<{ id: number; status: string }>(
    `/admin/certification/requests/${requestId}/reject`,
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to reject certification request");
  }
  return response;
}

// Get trainer details by ID
async function getPlatformTrainerDetails(
  trainerId: number,
): Promise<ApiResponse<PlatformTrainerDetails>> {
  const response = await apiClient.get<PlatformTrainerDetails>(
    `/admin/trainers/${trainerId}/platform-details`,
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to get trainer details");
  }
  return response;
}

// Create user (admin only)
async function createUser(
  data: CreateUserRequestInterface,
): Promise<ApiResponse<CreateUserResponse>> {
  const response = await apiClient.post<CreateUserResponse>("/admin/users", {
    body: data,
  });
  return withDefaultError(response, "Failed to create user");
}

// Create a trainer profile for an existing user. The backend creates the
// Stripe Connect account via the Stripe API; the profile is created empty so
// the trainer fills in their own documents/info later.
async function createTrainerProfile(
  userId: number
): Promise<ApiResponse<TrainerProfile>> {
  const response = await apiClient.post<TrainerProfile>(
    `/admin/users/${userId}/trainer-profile`
  );
  return withDefaultError(response, "Failed to create trainer profile");
}

async function resetStripeConnect(
  userId: number,
): Promise<
  ApiResponse<{
    userId: number;
    previousAccountId: string | null;
    disconnected: boolean;
    stripeAccountDeleted: boolean;
  }>
> {
  const response = await apiClient.delete<{
    userId: number;
    previousAccountId: string | null;
    disconnected: boolean;
    stripeAccountDeleted: boolean;
  }>(`/admin/users/${userId}/stripe-connect`);
  return withDefaultError(response, "Failed to reset Stripe Connect");
}

// Update user (admin only)
async function updateUser(
  userId: number,
  data: UpdateUserRequest,
): Promise<UpdateUserResponse> {
  const response = await apiClient.put<UpdateUserResponse["data"]>(
    `/admin/users/${userId}`, {
        body: data,
      });

  if (!response.ok || !response.data) {
    throw new Error(response.error || "Failed to update user");
  }

  return {
    success: true,
    message: response.message || "User updated successfully",
    data: response.data,
  };
}

// Delete user (admin only)
async function deleteUser(
  userId: number,
): Promise<ApiResponse<DeleteUserResponse>> {
  const response = await fetch(`${API_URL}/admin/users/${userId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to delete user");
  }

  return response.json();
}

async function getActiveUsersByHourToday(): Promise<ActiveUsersByHourResponse> {
  const response = await fetch(`${API_URL}/admin/users/active-by-hour/today`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to get active users data");
  }

  return response.json();
}

// Get active users by hour for a specific date
async function getActiveUsersByHourForDate(
  date: string,
): Promise<ActiveUsersByHourResponse> {
  const response = await fetch(
    `${API_URL}/admin/users/active-by-hour/date?date=${date}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(
      error.message || "Failed to get active users data for date",
    );
  }

  return response.json();
}

// Get recent users with optional role filter
async function getRecentUsers(
  limit?: number,
  role?: string,
): Promise<RecentUsersResponse> {
  const params = new URLSearchParams();
  if (limit) params.append("limit", limit.toString());
  if (role) params.append("role", role);

  const url = `${API_URL}/admin/users/recent${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to get recent users data");
  }

  return response.json();
}

// Fetch total users by role from admin endpoint
async function fetchUsersTotalByRole(
  monthAgo: number,
  role?: string,
): Promise<ApiResponse<UserCountResponse>> {
  const params = new URLSearchParams({ monthAgo: monthAgo.toString() });
  if (role) {
    params.append("role", role);
  }

  const response = await apiClient.get<UserCountResponse>(
    `/admin/users/total-by-role?${params.toString()}`,
  );
  if (!response.ok) {
    return withDefaultError(response, "Failed to fetch users total by role");
  }
  return response;
}

export type RefreshTokenResponse = {
  accessToken: string;
  refreshToken?: string;
};

async function refreshAccessToken(
  refreshToken: string,
): Promise<RefreshTokenResponse> {
  const response = await fetch(`${API_URL}/auth/refresh-token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      (error as { message?: string }).message || "Token refresh failed",
    );
  }

  const body = await response.json();
  return unwrapAuthPayload<RefreshTokenResponse>(
    body,
    "Impossible de rafraîchir la session",
  );
}

export {
  getUserDetails,
  getTrainerDetails,
  getCertificationCriteria,
  updateCertificationCriteria,
  getCertificationRequests,
  getTrainerCertificationStatus,
  approveTrainerCertification,
  setTrainerQualityApproval,
  getCertificationReviewQueue,
  revokeTrainerCertification,
  rejectCertificationRequest,
  createUser,
  createTrainerProfile,
  resetStripeConnect,
  updateUser,
  deleteUser,
  getActiveUsersByHourToday,
  getActiveUsersByHourForDate,
  loginUser,
  refreshAccessToken,
  getRecentUsers,
  fetchUsersTotalByRole,
  getUsersList,
  getPlatformTrainerDetails,
};
